// View model + pure helpers for the weekly posts view (feature 004-weekly-posts-view).
// Derives a read-only card model from an existing PeerActivityType; no new data.
import {format} from "date-fns";
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {MainFileType, MainFileVideoTypes} from "../../redux/Main/File/types";
import {PeerActivityStatus, PeerActivityStatusClass} from "../../redux/Content/PeerActivityStatus/types";
import {convertToMomentLocale, getEnumKeyByValue} from "../../helpers/functions";
import {PostType, resolvePostType} from "./postType";

// `handler` is attached to calendar events at runtime but is not on PeerActivityType.
type ActivityWithHandler = PeerActivityType & { handler?: { code?: string; name?: string } };

// A translator compatible with react-i18next's `t` (namespaced keys like "peerActivityStatus:F").
type Translate = (key: string) => string;

export interface MediaPreview {
    thumbUrl: string | null;
    hasMedia: boolean;
    isVideo: boolean;
    count: number;
    isCarousel: boolean;
}

export interface WeekPostCardModel {
    uuid: string;
    startTime: string;
    durationMin: number;
    isCollapsed: boolean;
    title: string;
    text: string;
    channelName: string;
    platformCode: string | null;
    platformName: string;
    typeName: string;
    typeColor: string;
    modeCode: string;
    status: string;
    statusClass: string;
    statusLabel: string;
    media: MediaPreview;
    // Feature 005 (intelly): derived presentational fields.
    channelInitials: string;
    channelColorKey: string;
    isDraft: boolean;
    isPublished: boolean;
    mediaLabel: string;
    // Feature 007 (monthly posts view): coarse post-type classification.
    postType: PostType;
}

// Up to two initials from a channel name, ignoring @/punctuation (e.g. "Brand Studio" → "BS", "@brandstudio" → "BR").
export function channelInitials(name: string): string {
    const clean = (name || "").replace(/[^a-zA-Z0-9À-ɏ ]/g, " ").trim();
    if (!clean) return "?";
    const words = clean.split(/\s+/);
    const raw = words.length > 1 ? (words[0][0] + words[1][0]) : clean.slice(0, 2);
    return raw.toUpperCase();
}

// Deterministic bucket "1".."5" from a stable key, for the --wb-av-N avatar palette.
export function channelColorKey(key: string): string {
    const s = key || "";
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return String((h % 5) + 1);
}

const VIDEO_EXT: string[] = Object.values(MainFileVideoTypes);

function isVideoFile(file?: MainFileType): boolean {
    if (!file) return false;
    const ct = (file.contentType || "").toLowerCase();
    if (ct.startsWith("video") || VIDEO_EXT.includes(ct)) return true;
    const url = (file.url || "").toLowerCase().split("?")[0];
    return VIDEO_EXT.some((ext) => url.endsWith("." + ext));
}

export function resolveMediaPreview(activity: PeerActivityType): MediaPreview {
    const list: MainFileType[] = (activity.media && activity.media.length ? activity.media : []) as MainFileType[];
    const first = list[0] || activity.cover?.[0] || activity.avatar?.[0];
    const thumbUrl = first?.url || null;
    const count = activity.media?.length || 0;
    return {
        thumbUrl,
        hasMedia: !!thumbUrl,
        isVideo: isVideoFile(list[0]),
        count,
        isCarousel: count > 1,
    };
}

// A post that is already published (FINAL) or currently being published (PUBLISHING) must not be
// dragged to another day — its publish slot is fixed or already in flight. Both the week and month
// boards use this to disable react-beautiful-dnd dragging for such cards. Note ON_PUBLISH ("OP") is
// a *scheduled/queued* state (shown as "Scheduled"), so those posts stay reschedulable.
export function isRescheduleLocked(status: string): boolean {
    return status === PeerActivityStatus.STATUS_FINAL
        || status === PeerActivityStatus.STATUS_PUBLISHING;
}

export function resolveStatus(activity: PeerActivityType, t: Translate): { status: string; statusClass: string; statusLabel: string } {
    const status = activity.status || "";
    const key = status ? getEnumKeyByValue(PeerActivityStatus, status) : undefined;
    // Statuses without an explicit contextual colour (READY/UNPUBLISHED/PUBLISHING) fall back to a neutral tone.
    const statusClass = (key && (PeerActivityStatusClass as Record<string, string>)[key]) || "secondary";
    const statusLabel = status ? t("peerActivityStatus:" + status) : "";
    return {status, statusClass, statusLabel};
}

export function mapActivityToWeekCardModel(
    activity: PeerActivityType,
    start: Date | null,
    end: Date | null,
    t: Translate,
    language: string,
): WeekPostCardModel {
    const handler = (activity as ActivityWithHandler).handler || {};
    const durationMin = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : 30;
    const {status, statusClass, statusLabel} = resolveStatus(activity, t);
    const channelName = activity.channel?.name || "";
    const media = resolveMediaPreview(activity);
    // Video → "Video"; any images → the image count (e.g. "1", "3"); no media → "".
    const mediaLabel = media.isVideo ? "Video" : (media.hasMedia ? String(media.count) : "");
    return {
        uuid: activity.uuid || "",
        startTime: start ? format(new Date(start), "HH:mm", {locale: convertToMomentLocale(language)}) : "",
        durationMin,
        isCollapsed: durationMin > 0 && durationMin <= 15,
        title: activity.title || activity.name || "",
        text: activity.text || "",
        channelName,
        platformCode: handler.code || null,
        platformName: handler.name || "",
        typeName: activity.type?.name || "",
        typeColor: activity.type?.color || "#878a99",
        modeCode: activity.mode?.code || "",
        status,
        statusClass,
        statusLabel,
        media,
        channelInitials: channelInitials(channelName || handler.name || ""),
        channelColorKey: channelColorKey(activity.channel?.uuid || channelName || activity.uuid || ""),
        isDraft: status === PeerActivityStatus.STATUS_UNPUBLISHED,
        isPublished: status === PeerActivityStatus.STATUS_FINAL,
        mediaLabel,
        postType: resolvePostType(activity),
    };
}
